import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const LOCATIONS = [
  { slug: "sarjapur-road", name: "Sarjapur Road" },
  { slug: "whitefield", name: "Whitefield" },
  { slug: "electronic-city", name: "Electronic City" },
  { slug: "bannerghatta-road", name: "Bannerghatta Road" },
  { slug: "north-bangalore", name: "North Bangalore" },
];

export default function Footer() {
  const { settings } = useSettings();
  const s = settings || {};
  const social = s.social || {};
  return (
    <footer className="bg-navy text-gray-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            {s.logo_url && <img src={s.logo_url} alt={s.brand_name} className="h-12 w-auto object-contain bg-white/5 rounded" />}
            <span className="font-display text-xl font-bold text-white">{s.brand_name || "Mathura Homes"}</span>
          </div>
          <p className="text-sm leading-relaxed text-gray-400">
            Your trusted real estate channel partner and property consultant in Bangalore. We help you compare projects, arrange site visits and complete your purchase with confidence.
          </p>
          <div className="flex gap-3 mt-5">
            {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 rounded-lg bg-white/10 hover:bg-gold hover:text-navy transition-colors"><Instagram className="w-4 h-4" /></a>}
            {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2 rounded-lg bg-white/10 hover:bg-gold hover:text-navy transition-colors"><Facebook className="w-4 h-4" /></a>}
            {social.linkedin && <a href={social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="p-2 rounded-lg bg-white/10 hover:bg-gold hover:text-navy transition-colors"><Linkedin className="w-4 h-4" /></a>}
            {social.youtube && <a href={social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="p-2 rounded-lg bg-white/10 hover:bg-gold hover:text-navy transition-colors"><Youtube className="w-4 h-4" /></a>}
            {!social.instagram && !social.facebook && !social.linkedin && !social.youtube && (
              <span className="text-xs text-gray-500">[Add social links in admin]</span>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/properties" className="hover:text-gold transition-colors">Properties</Link></li>
            <li><Link to="/about" className="hover:text-gold transition-colors">About Us</Link></li>
            <li><Link to="/services" className="hover:text-gold transition-colors">Services</Link></li>
            <li><Link to="/blog" className="hover:text-gold transition-colors">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Popular Locations</h4>
          <ul className="space-y-2.5 text-sm">
            {LOCATIONS.map((l) => (
              <li key={l.slug}><Link to={`/properties-in-${l.slug}`} className="hover:text-gold transition-colors">{l.name}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Get In Touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-gold shrink-0" /><a href={`tel:${s.phone}`} className="hover:text-gold">{s.phone || "[Phone]"}</a></li>
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-gold shrink-0" /><a href={`mailto:${s.email}`} className="hover:text-gold break-all">{s.email || "[Email]"}</a></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-gold shrink-0" /><span>{s.address || "[Office Address]"}</span></li>
          </ul>
          {s.rera_number && <p className="mt-4 text-xs text-gray-500">RERA: {s.rera_number}</p>}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            <strong className="text-gray-400">Disclaimer:</strong> Mathura Homes is a real estate channel partner / property consultant and is not the developer of the projects displayed. All prices, availability, specifications, images and offers are indicative, subject to change and must be verified with the respective developer. Images are for representation only.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} {s.brand_name || "Mathura Homes"}. All rights reserved.</p>
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <Link to="/legal/privacy-policy" className="hover:text-gold">Privacy Policy</Link>
              <Link to="/legal/terms-and-conditions" className="hover:text-gold">Terms</Link>
              <Link to="/legal/disclaimer" className="hover:text-gold">Disclaimer</Link>
              <Link to="/legal/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
              <Link to="/legal/rera-disclaimer" className="hover:text-gold">RERA Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
